import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { fieldTypes } from '@app/consts';
import { MetadataFormService } from '@app/core/services/metadata/metadata-form.service';
import { MetadataTableService } from '@app/core/services/metadata/metadata-table.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { CultureService } from '@app/core/services/system-language/culture.service';
import { TableList } from '@app/modules/lookup/models/lookup.model';
import { LookupService } from '@app/modules/lookup/services/lookup.service';
import { TableFieldVerbose } from '@app/modules/security-setup/models/table-field.model';
import { SecuritySetupService } from '@app/modules/security-setup/services/security-setup.service';
import { EmployeeSecurityService } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-security/services/employee-security.service';
import { SelectSearchState } from '@app/shared/components/select-search/select-search-state';
import { LocalizationFormService } from '@app/shared/services/localization-form.service';
import { Culture } from '@app/shared/models/system-language/culture.model';
import moment from 'moment';
import { Observable, forkJoin, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { FormElement, FormElementType, FormElementVerbose, TableField } from '../../models/form.model';

// Properties that have their own controls in the dialog; any others are custom and edited under Advanced
const KNOWN_PROPERTIES = ['requiredField', 'disabled', 'hidden', 'minLength', 'maxLength', 'lookupGroup', 'order', 'formControl'];

@Component({
  selector: 'app-form-element-dialog-v2',
  templateUrl: './form-element-dialog-v2.component.html',
  styleUrls: ['./form-element-dialog-v2.component.scss']
})
export class FormElementDialogV2Component implements OnInit {
  public fieldTypes: typeof fieldTypes = fieldTypes;
  form: UntypedFormGroup;
  formId: string;
  cultures: Culture[];
  loading: boolean;
  formElementTypes: FormElementType[] = [];
  tableLists: Observable<TableList[]>;
  formElementVerbose: FormElementVerbose;
  formElementId: string;
  parentId: string;
  formTableId: string;
  tableFieldOptions: TableField[] = [];
  parentElements: FormElement[];
  order: number;
  saving: boolean = false;
  showErrors: boolean = false;
  advancedOpen: boolean = false;

  // Describe the label and help text form arrays to app-localization-text-form-field
  labelField = {
    formControl: 'name',
    localizationValueParam: 'text',
    text: 'Label',
    disabled: 'false'
  } as FormElement;

  helpTextField = {
    formControl: 'tooltipText',
    localizationValueParam: 'text',
    text: 'Help text',
    disabled: 'false'
  } as FormElement;

  typeSearch = new SelectSearchState<FormElementType>(
    () => this.formElementTypes,
    type => type.name,
    () => this.formElementTypes.find(type => type.id === this.form?.get('formElementType').value)
  );

  tableFieldSearch = new SelectSearchState<TableField>(
    () => this.tableFieldOptions,
    field => field.name,
    () => this.tableFieldOptions.find(field => field.id === this.form?.get('tableField').value)
  );

  allowedFieldTypes = fieldTypes;
  loadingTableField: boolean;
  selectedFieldVerboseFormId: string = 'frm_O734S5cU26vwCY';
  selectedFieldVerbose: TableFieldVerbose;
  selectedFieldVerboseFormData: any;

  constructor(
    private snackbarService: SnackbarService,
    private metadataFormService: MetadataFormService,
    private fb: UntypedFormBuilder,
    private cultureService: CultureService,
    private lookupService: LookupService,
    private metadataTableService: MetadataTableService,
    private securitySetupService: SecuritySetupService,
    private router: Router,
    private employeeSecurityService: EmployeeSecurityService,
    private localizationFormService: LocalizationFormService,
    private dialogRef: MatDialogRef<FormElementDialogV2Component>,
    @Inject(MAT_DIALOG_DATA) data) {
      this.formElementId = data.formElementId;
      this.parentId = data.parentId;
      this.formId = data.formId;
      this.formTableId = data.formTableId;
      this.order = data.order;
    }

  ngOnInit(): void {
    this.loadForm();

    this.getTableLookups();
    this.getTableFields();
    this.getFormElementTypes();
    this.getParentElements();
    this.cultureService.getCultures().subscribe( res => { this.cultures = res } );
  }

  getTableLookups() {
    this.tableLists = this.lookupService.getTableLists();
  }

  get isEdit(): boolean {
    return this.formElementId !== null && this.formElementId !== undefined;
  }

  getTableFields() {
    if (!this.formTableId) {
      return;
    }

    this.metadataTableService.getTableFields(this.formTableId, 1000, 0)
      .subscribe(res => {
        this.tableFieldOptions = res?.data ?? [];
        this.tableFieldSearch.refresh();
      });
  }

  get formFieldId() {
    return this.form.controls["tableField"].value;
  }

  getFormElementTypes() {
    this.metadataFormService.getFormElementTypes(1000, 0)
    .subscribe(
      res => {
        this.formElementTypes = [...res.data].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        this.typeSearch.refresh();
      });
  }

  // Loads the element being edited, and the current culture so translations list the user's language first
  loadForm() {
    forkJoin([
      this.isEdit ? this.metadataFormService.getFormElement(this.formId, this.formElementId) : of(null),
      this.employeeSecurityService.currentCulture$(),
    ])
    .subscribe(
      ([formElement]) => {
        this.formElementVerbose = formElement ?? undefined;
        this.createForm();
      }
    )
  }

  createForm() {
    this.form = this.fb.group({
      id: [this.formElementVerbose ? this.formElementVerbose.id : null],
      asOf: [moment().format()],
      name: this.localizationFormService.buildLocalizationArray(this.formElementVerbose?.text),
      // Help text is optional, so unlike the name it may have no rows
      tooltipText: this.fb.array(
        this.localizationFormService.sortByCulture(this.formElementVerbose?.tooltipText)
          .map(localization => this.localizationFormService.createLocalizationGroup(localization.culture, localization.text))
      ),
      properties: this.fb.array([]),
      formElementType: [this.formElementVerbose ? this.formElementVerbose.formElementType?.id : null, Validators.required],
      parentFormElement: [this.formElementVerbose ? this.formElementVerbose.parentFormElement?.id : this.parentId],
      tableField: [this.formElementVerbose ? this.formElementVerbose.tableField?.id : null],
      formId: [this.formId],
      changeReason: [''],
      changeReasonComments: [''],
    });

    if(this.formElementVerbose){
      this.addMinAndMaxProperties();

      this.getTableFieldDetails();

      this.formElementVerbose.properties?.forEach(
        property => {
          this.addNewProperty(property.property, property.value);
        }
        );

      this.addMissingDefaultValues()
    }
    else {
      this.setDeafultProperties();
    }
  }

  // If there are missing default properties when editing an existing form element then add them
  addMissingDefaultValues() {
    let requiredField = this.properties.value.findIndex( element => element.property === 'requiredField');
    let hidden = this.properties.value.findIndex( element => element.property === 'hidden');
    let disabled = this.properties.value.findIndex( element => element.property === 'disabled');
    let formControl = this.properties.value.findIndex( element => element.property === 'formControl');
    let order = this.properties.value.findIndex( element => element.property === 'order');

    requiredField === -1 ? this.addNewProperty('requiredField', 'false') : null;
    hidden === -1 ? this.addNewProperty('hidden', 'false') : null;
    disabled === -1 ? this.addNewProperty('disabled', 'false') : null;
    formControl === -1 ? this.addNewProperty('formControl', '') : null;
    order === -1 ? this.addNewProperty('order', this.order.toString()) : null;
  }

  getTableFieldDetails() {
    if(this.formTableId !== null && (this.formFieldId !== null && this.formFieldId !== undefined)){
      this.loadingTableField = true;

      this.securitySetupService.getField(this.formTableId, this.formFieldId)
      .pipe(
        finalize(()=>{
          this.loadingTableField = false;
        })
      )
      .subscribe(
        res => {
          this.selectedFieldVerbose = res;

          this.selectedFieldVerboseFormData = {
            id: this.selectedFieldVerbose ? this.selectedFieldVerbose.id : null,
            changeReason: '',
            changeReasonComments: '',
            name: this.selectedFieldVerbose ? this.selectedFieldVerbose.name : null,
            enabled: this.selectedFieldVerbose ? this.selectedFieldVerbose.enabled : null,
            requiredField: this.selectedFieldVerbose ? this.selectedFieldVerbose.requiredField : null,
            minimumValue: this.selectedFieldVerbose ? this.selectedFieldVerbose.minimumValue : null,
            maximumValue: this.selectedFieldVerbose ? this.selectedFieldVerbose.maximumValue : null,
            defaultValue: this.selectedFieldVerbose ? this.selectedFieldVerbose.defaultValue : null,
            format: this.selectedFieldVerbose ? this.selectedFieldVerbose.format : null,
            originalCreatedOn: this.selectedFieldVerbose ? this.selectedFieldVerbose.version?.createdOn : null,
            asOf: moment().format(),
          };
        }
      );
    }
  }

  // If the form element doesnt have minLength and maxLength value in the properties array add them
  addMinAndMaxProperties() {
    let hasMin = this.formElementVerbose.properties?.findIndex( property =>
      property.property === 'minLength'
    )

    let hasMax = this.formElementVerbose.properties?.findIndex( property =>
      property.property === 'maxLength'
    )

    if(hasMin === -1){
      this.addNewProperty('minLength', null);
    }

    if(hasMax === -1){
      this.addNewProperty('maxLength', null);
    }
  }

  setDeafultProperties() {
    this.addNewProperty('requiredField', 'false');
    this.addNewProperty('hidden', 'false');
    this.addNewProperty('disabled', 'false');
    this.addNewProperty('formControl', '');
    this.addNewProperty('minLength', null);
    this.addNewProperty('maxLength', null);

    if(this.order !== null){
      this.addNewProperty('order', this.order.toString());
    }

  }

  get localizations() {
    return this.form.controls["name"] as UntypedFormArray;
  }

  addNewLocalization() {
    this.localizations.push(this.localizationFormService.createLocalizationGroup());
  }

  deleteLocalization(index: number) {
    this.localizations.removeAt(index);
  }

    get tooltipLocalizations() {
        return this.form.controls["tooltipText"] as UntypedFormArray;
    }

  // The first help text row starts in the user's language; further translations start blank
  addNewTooltipLocalization() {
    const culture = this.tooltipLocalizations.length === 0 ? this.employeeSecurityService.currentUserCulture?.id : undefined;

    this.tooltipLocalizations.push(this.localizationFormService.createLocalizationGroup(culture));
  }

    deleteTooltipLocalization(index: number) {
    this.tooltipLocalizations.removeAt(index);
  }

  addLocalizationFromComponent(formControl: string) {
    if (formControl === 'name') {
      this.addNewLocalization();
    }
    else if (formControl === 'tooltipText') {
      this.addNewTooltipLocalization();
    }
  }

  deleteLocalizationFromComponent(event: { index: number; formControl: string }) {
    if (event.formControl === 'name') {
      this.deleteLocalization(event.index);
    }
    else if (event.formControl === 'tooltipText') {
      this.deleteTooltipLocalization(event.index);
    }
  }

  get properties() {
    return this.form.controls["properties"] as UntypedFormArray;
  }

  addNewProperty(property?: string, value?: string) {
    let val;

    // if the property value is supposed to be a boolean convert it from string to boolean
    (value === "true" || value === "false")
      ? val = (value === 'true')
      : val = value;

    const propertyForm = this.fb.group({
      property: [property || '', Validators.required],
      value: [val || '']
    });

    this.properties.push(propertyForm);
  }

  deleteProperty(index: number) {
    this.properties.removeAt(index);
  }

  // The form group of a named property, e.g. 'requiredField', or undefined when the element doesn't have it
  propertyGroup(name: string): UntypedFormGroup {
    return this.properties.controls.find(control => control.value.property === name) as UntypedFormGroup;
  }

  // Indexes (into the properties form array) of the properties without their own control
  get customPropertyIndexes(): number[] {
    return this.properties.controls
      .map((control, index) => KNOWN_PROPERTIES.includes(control.value.property) ? -1 : index)
      .filter(index => index !== -1);
  }

  addCustomProperty() {
    this.addNewProperty();
    this.advancedOpen = true;
  }

  get customPropertiesInvalid(): boolean {
    return this.customPropertyIndexes.some(index => this.properties.at(index).invalid);
  }

  deletePropertyByName(name: string) {
    let index = this.properties.value.findIndex(prop => prop.property === name);

    if(index !== -1) {
      this.properties.removeAt(index);
    }
  }

  onFormElementTypeSelection(event) {
    if(event.value === fieldTypes.DROPDOWN) {
      this.addNewProperty('lookupGroup', '');
    }
    else {
      this.deletePropertyByName('lookupGroup')
    }
  }

  getParentElements() {
    this.metadataFormService.getFormElements(this.formId, 1000, 0)
    .subscribe(
      res => {
        this.parentElements = res.data.filter( (formElement: FormElement) => formElement.formElementType.id === fieldTypes.CONTAINER )
      }
    )
  }

  convertFormPropertyValuesToStrings() {
    this.form.value.properties.forEach( property => {
      property.value !== null
        ? property.value = property.value.toString()
        : null
    })
  }

  save() {
    if (this.form.invalid) {
      this.showErrors = true;
      this.form.markAllAsTouched();

      if (this.customPropertiesInvalid) {
        this.advancedOpen = true;
      }

      return;
    }

    if (this.saving) {
      return;
    }

    this.saving = true;
    this.convertFormPropertyValuesToStrings();

    if(this.formElementId !== null) {
      this.metadataFormService.updateFormElement(this.formId, this.formElementVerbose.id, this.form.value)
      .pipe(
        finalize( () => this.saving = false )
      )
      .subscribe(
        res => {
          this.snackbarService.openSnackBar('Form Element updated successfully', 'clear', 'success');
          this.dialogRef.close(true);
        }
      )
    }
    else {
      this.metadataFormService.postFormElement(this.formId, this.form.value)
      .pipe(
        finalize( () => this.saving = false )
      )
      .subscribe(
        res => {
          this.snackbarService.openSnackBar('Form Element created successfully', 'clear', 'success');
          this.dialogRef.close(true);
        }
      )
    }
  }

  navigateToTableFieldEditor() {
    this.close();
    this.router.navigateByUrl(`SiteSettings/TableFieldEditor/${this.formTableId}/${this.formFieldId}`);
  }

  close() {
    this.dialogRef.close();
  }

  deleteAllTooltipLocalizations(): void {
    this.tooltipLocalizations.clear();
  }

}
